import { forwardRef, useMemo } from 'react'
import classnames from 'classnames'
import Image, { StaticImageData } from 'next/image'

import { PhotoIcon } from '../../assets/icons/icons'

import styles from './avatar.module.scss'

type avatarSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

type AvatarPropsWithEmptyState = {
  hasEmptyState: true
  className?: string
  image?: string | null | StaticImageData
  secondaryImage?: string | null
  name?: never
  secondaryName?: never
  size?: avatarSize
  priority?: boolean
  labelClassName?: string
  title?: string
}

type AvatarPropsNoEmptyState = {
  hasEmptyState?: false
  className?: string
  image?: string | null | StaticImageData
  secondaryImage?: string | null
  name: string
  secondaryName?: string | null
  size?: avatarSize
  priority?: boolean
  labelClassName?: string
  title?: string
}

export type AvatarProps = AvatarPropsNoEmptyState | AvatarPropsWithEmptyState

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      image,
      secondaryImage,
      name,
      secondaryName,
      size = 'md',
      hasEmptyState,
      priority,
      labelClassName,
      title,
    },
    ref
  ) => {
    const firstNameLetter = name?.[0]

    const sizes = useMemo(() => {
      switch (size) {
        case 'sm':
          return {
            width: 16,
            height: 16,
          }
        case 'lg':
          return {
            width: 32,
            height: 32,
          }
        case 'xl':
          return {
            width: 48,
            height: 48,
          }
        case 'xxl':
          return {
            width: 80,
            height: 80,
          }
        default:
          return {
            width: 24,
            height: 24,
          }
      }
    }, [size])

    const secondarySizes = useMemo(() => {
      switch (size) {
        case 'lg':
          return {
            width: 12,
            height: 12,
          }
        case 'xl':
          return {
            width: 16,
            height: 16,
          }
        case 'xxl':
          return {
            width: 24,
            height: 24,
          }
        default:
          return {
            width: 8,
            height: 8,
          }
      }
    }, [size])

    return image ? (
      <div className={classnames(styles['images-wrapper'], className)} ref={ref}>
        <div className={classnames(styles['image-wrapper'], styles[size])}>
          <Image
            src={image}
            alt={`The ${name}'s avatar`}
            {...sizes}
            priority={priority}
            className={styles['image']}
            title={title}
          />
        </div>
        {secondaryImage && size !== 'sm' && (
          <div className={classnames(styles['secondary-image-wrapper'], styles[size])}>
            <Image
              src={secondaryImage}
              alt={`The ${secondaryName}'s avatar`}
              {...secondarySizes}
              priority={priority}
              className={styles['secondary-image']}
              title={title}
            />
          </div>
        )}
      </div>
    ) : (
      <div
        className={classnames(
          styles[size],
          styles['image-wrapper'],
          styles['abbreviation-wrapper'],
          className
        )}
        title={title}
      >
        {firstNameLetter && (
          <span className={classnames(styles['abbreviation'], labelClassName)}>
            {firstNameLetter}
          </span>
        )}

        {hasEmptyState && <PhotoIcon className={styles['icon']} />}
      </div>
    )
  }
)

export default Avatar
